import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const [role, setRole] = useState("sponsor");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [siren, setSiren] = useState("");
    const [phone, setPhone] = useState("");
    const [referralCode, setReferralCode] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const ref = params.get("ref");
        if (ref) setReferralCode(ref);
    }, [params]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        const cleanedPhone = phone.replace(/[\s.\-]/g, "");
        if (!/^0\d{9}$/.test(cleanedPhone)) {
            setErr("Numéro de téléphone invalide (10 chiffres, ex. 06 12 34 56 78).");
            return;
        }
        if (role === "sponsor") {
            const cleaned = siren.replace(/\s/g, "");
            if (!/^\d{9}$/.test(cleaned)) {
                setErr("Le SIREN doit contenir 9 chiffres.");
                return;
            }
        }
        setLoading(true);
        const payload = { name, email, password, role, phone: cleanedPhone };
        if (role === "sponsor") {
            payload.siren = siren.replace(/\s/g, "");
            if (referralCode.trim()) payload.referral_code = referralCode.trim();
        }
        const res = await register(payload);
        setLoading(false);
        if (res.ok) {
            toast.success(role === "sponsor"
                ? "Compte créé. En attente de validation par l'admin."
                : "Compte créé !");
            nav("/dashboard");
        } else setErr(res.error);
    };

    return (
        <div className="min-h-[78vh] flex items-center justify-center px-6 py-16">
            <div className="w-full max-w-md">
                <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">
                    Inscription
                </div>
                <h1 className="font-display text-5xl uppercase tracking-tight mb-8">
                    Rejoignez le mouvement
                </h1>

                <form onSubmit={submit} className="card-tu space-y-5">
                    <div>
                        <label className="label-tu">Je suis…</label>
                        <div className="grid grid-cols-2 gap-px bg-zinc-800 border border-zinc-800">
                            {[
                                { v: "sponsor", l: "Sponsor" },
                                { v: "club", l: "Club" },
                            ].map((r) => (
                                <button
                                    type="button"
                                    key={r.v}
                                    data-testid={`role-${r.v}`}
                                    onClick={() => setRole(r.v)}
                                    className={[
                                        "py-3 uppercase font-bold tracking-widest text-xs transition-colors",
                                        role === r.v
                                            ? "bg-gradient-to-r from-orange-600 to-red-600 text-white"
                                            : "bg-zinc-950 text-zinc-400 hover:text-white",
                                    ].join(" ")}
                                >
                                    {r.l}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <label className="label-tu">{role === "club" ? "Nom du club" : "Raison sociale"}</label>
                        <input
                            data-testid="register-name"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="input-tu"
                        />
                    </div>
                    <div>
                        <label className="label-tu">Email</label>
                        <input
                            data-testid="register-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="input-tu"
                        />
                    </div>
                    <div>
                        <label className="label-tu">Mot de passe (6+ car.)</label>
                        <input
                            data-testid="register-password"
                            type="password"
                            required
                            minLength={6}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="input-tu"
                        />
                    </div>
                    <div>
                        <label className="label-tu">
                            Téléphone du contact {role === "club" ? "club" : "sponsor"}
                        </label>
                        <input
                            data-testid="register-phone"
                            type="tel"
                            required
                            value={phone}
                            onChange={(e) => setPhone(e.target.value.replace(/[^0-9 .\-]/g, ""))}
                            placeholder="06 12 34 56 78"
                            className="input-tu"
                            inputMode="tel"
                            autoComplete="tel"
                        />
                        <p className="text-[11px] text-zinc-500 mt-1">
                            10 chiffres, format français (ex. 06 12 34 56 78).
                        </p>
                    </div>

                    {role === "sponsor" && (
                        <>
                            <div>
                                <label className="label-tu">SIREN (9 chiffres)</label>
                                <input
                                    data-testid="register-siren"
                                    required
                                    value={siren}
                                    onChange={(e) => setSiren(e.target.value.replace(/[^0-9 ]/g, ""))}
                                    placeholder="123 456 789"
                                    className="input-tu"
                                    inputMode="numeric"
                                />
                                <p className="text-[11px] text-zinc-500 mt-1">
                                    Votre SIREN sera vérifié par l'admin avant activation de votre compte.
                                </p>
                            </div>
                            <div>
                                <label className="label-tu">Code parrainage (optionnel)</label>
                                <input
                                    data-testid="register-referral"
                                    value={referralCode}
                                    onChange={(e) => setReferralCode(e.target.value)}
                                    placeholder="Ex. SPONSOR-DEMO"
                                    className="input-tu"
                                />
                                <p className="text-[11px] text-zinc-500 mt-1">
                                    +100 pts pour vous et votre parrain.
                                </p>
                            </div>
                        </>
                    )}

                    {err && (
                        <div className="text-sm text-red-500 border border-red-900/50 bg-red-950/30 p-3">
                            {err}
                        </div>
                    )}
                    <button
                        data-testid="register-submit"
                        disabled={loading}
                        className="btn-primary w-full"
                    >
                        {loading ? "Création…" : "Créer mon compte"}
                    </button>
                </form>
                <p className="mt-6 text-center text-sm text-zinc-400">
                    Déjà un compte ?{" "}
                    <Link to="/login" data-testid="goto-login" className="text-orange-500 font-bold uppercase tracking-widest text-xs">
                        Connexion
                    </Link>
                </p>
            </div>
        </div>
    );
}
